/**
 * Parse a passage ref string to extract chapter and verse ranges.
 * Handles multiple formats:
 * - "1:1-8" (same chapter)
 * - "1:1-2:8" (different chapters)
 * - "1:1" (single verse)
 * - "1" (whole chapter)
 * 
 * @param ref - Passage reference string
 * @returns Object with chapterStart, verseStart, chapterEnd, verseEnd, or null if invalid
 */
export function parseRef(ref: string | undefined | null): VerseRange | null {
    if (!ref || typeof ref !== 'string') {
        return null;
    }

    const trimmedRef = ref.trim();

    // Try same chapter format first: "1:1-8"
    const sameChapterMatch = trimmedRef.match(/^(\d+):(\d+)-(\d+)$/);
    if (sameChapterMatch) {
        const chapter = parseInt(sameChapterMatch[1], 10);
        return {
            chapterStart: chapter,
            verseStart: parseInt(sameChapterMatch[2], 10),
            chapterEnd: chapter,
            verseEnd: parseInt(sameChapterMatch[3], 10)
        };
    }

    // Try different chapters format: "1:1-2:8"
    const differentChaptersMatch = trimmedRef.match(/^(\d+):(\d+)-(\d+):(\d+)$/);
    if (differentChaptersMatch) {
        return {
            chapterStart: parseInt(differentChaptersMatch[1], 10),
            verseStart: parseInt(differentChaptersMatch[2], 10),
            chapterEnd: parseInt(differentChaptersMatch[3], 10),
            verseEnd: parseInt(differentChaptersMatch[4], 10)
        };
    }

    // Try single verse format: "1:1"
    const singleVerseMatch = trimmedRef.match(/^(\d+):(\d+)$/);
    if (singleVerseMatch) {
        const chapter = parseInt(singleVerseMatch[1], 10);
        const verse = parseInt(singleVerseMatch[2], 10);
        return {
            chapterStart: chapter,
            verseStart: verse,
            chapterEnd: chapter,
            verseEnd: verse
        };
    }

    // Try whole chapter format: "1"
    const wholeChapterMatch = trimmedRef.match(/^(\d+)$/);
    if (wholeChapterMatch) {
        const chapter = parseInt(wholeChapterMatch[1], 10);
        return {
            chapterStart: chapter,
            verseStart: 1,
            chapterEnd: chapter,
            verseEnd: 999 // Arbitrary large number to represent end of chapter
        };
    }

    return null;
}

/**
 * Check if two verse ranges overlap
 * @param range1 - First verse range
 * @param range2 - Second verse range
 * @returns true if ranges overlap, false otherwise
 */
export function rangesOverlap(range1: VerseRange, range2: VerseRange): boolean {
    if (!range1 || !range2) return false;

    const start1 = range1.chapterStart * 1000 + range1.verseStart;
    const end1 = range1.chapterEnd * 1000 + range1.verseEnd;
    const start2 = range2.chapterStart * 1000 + range2.verseStart;
    const end2 = range2.chapterEnd * 1000 + range2.verseEnd;

    return start1 <= end2 && start2 <= end1;
}

/**
 * Validates if a reference is in a simple format we can link to
 * Valid formats: "8:14", "8:14-16", "8", "8-9"
 * Invalid: anything with semicolons, commas, parentheses, or descriptive text
 *
 * @param ref - Reference string to validate
 * @returns true if the reference is in a simple linkable format, false otherwise
 */
export function isValidSimpleRef(ref: string): boolean {
    if (!ref || typeof ref !== 'string') return false;

    // Check for invalid characters that indicate complex/descriptive references
    if (ref.includes(';') || ref.includes(',') || ref.includes('(') || ref.includes(')')) {
        return false;
    }

    // Check if it matches our expected patterns
    const simpleRefPattern = /^\d+(?::\d+)?(?:-\d+(?::\d+)?)?$/;
    return simpleRefPattern.test(ref.trim());
}

/**
 * Parse a full biblical reference (with book name) and create a link object
 * Handles complex formats:
 * - "1 Kings 17:17-24"
 * - "Isaiah 8:14"
 * - "1 "Kings 17:23 (LXX)" - with quotes and textual variants
 * 
 * @param fullRef - Full reference string including book name
 * @returns Object with url and displayText, or null if parsing fails
 */
export function createRefLink(fullRef: string): { url: string; displayText: string } | null {
    // Clean up the reference first
    let cleanRef = fullRef.trim();

    // Remove textual variant notations like (LXX), (MT), (Qumran), etc.
    cleanRef = cleanRef.replace(/\s*\([^)]*\)\s*$/, '');

    // Remove any stray quotes that might split the book name (e.g., 1 "Kings -> 1 Kings)
    cleanRef = cleanRef.replace(/"/g, '');

    // Extract book and reference from full reference (e.g., "1 Kings 17:17-24" -> book="1 Kings", ref="17:17-24")
    const match = cleanRef.match(/^(.+?)\s+([\d:,-]+)$/);

    if (!match) {
        return null;
    }

    const bookName = match[1];
    const ref = match[2];

    // Parse the reference to extract chapter and verse
    const parts = ref.split(':');
    let chapter: string;
    let verse: string | undefined;

    if (parts.length === 2) {
        chapter = parts[0];
        verse = parts[1].split('-')[0];
    } else {
        chapter = ref.split('-')[0];
    }

    const url = verse
        ? `/app/read/${bookName}/${chapter}?verse=${verse}`
        : `/app/read/${bookName}/${chapter}`;

    return { url, displayText: fullRef.trim() };
}

/**
 * Format a full biblical reference (with book name) as clickable link(s)
 * Handles multiple complex formats:
 * - Simple: "Isaiah 8:14"
 * - Multiple refs with slash: "Malachi 3:1 / Exodus 23:20"
 * - Same book, multiple refs: "Isaiah 35:5-6, 61:1"
 * - With textual variants: "1 Kings 17:23 (LXX)"
 *
 * @param fullRef - Full reference string including book name(s)
 * @param isDarkMode - Whether dark mode is enabled
 * @param setUrl - Function to navigate to a URL
 * @returns JSX element with clickable link(s)
 */
export function formatFullRefAsLink(
    fullRef: string,
    isDarkMode: boolean,
    setUrl: (url: string) => void
) {
    // Check if this is a multi-reference (contains / or , after a complete reference)
    const separators = [' / ', ' /', '/ ', '/'];
    let refs: string[] = [fullRef];
    let bookName: string | null = null;

    // Try splitting by slash first (different books)
    for (const sep of separators) {
        if (fullRef.includes(sep)) {
            refs = fullRef.split(sep).map(r => r.trim());
            break;
        }
    }

    // If no slash, try comma (could be same book or different books)
    if (refs.length === 1 && fullRef.includes(',')) {
        const parts = fullRef.split(',').map(p => p.trim());

        // Check if first part has a book name
        const firstMatch = parts[0].match(/^(.+?)\s+([\d:,-]+)$/);
        if (firstMatch) {
            bookName = firstMatch[1];

            // Check if subsequent parts start with digits (no book name)
            const needsBookName = parts.slice(1).every(part => /^\d/.test(part));

            if (needsBookName) {
                // Same book, prepend book name to parts without it
                refs = parts.map((part, index) => {
                    if (index === 0) return part;
                    return `${bookName} ${part}`;
                });
            } else {
                // Different books
                refs = parts;
            }
        }
    }

    // If we have multiple references, render them with separators
    if (refs.length > 1) {
        return (
            <span>
                {refs.map((ref, index) => {
                    const linkData = createRefLink(ref);
                    if (!linkData) {
                        return <span key={index} className="text-muted-foreground">{ref}</span>;
                    }
                    return (
                        <span key={index}>
                            {index > 0 && <span className="text-muted-foreground"> / </span>}
                            <span
                                onClick={() => setUrl(linkData.url)}
                                className={`cursor-pointer hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                            >
                                {linkData.displayText}
                            </span>
                        </span>
                    );
                })}
            </span>
        );
    }

    // Single reference
    const linkData = createRefLink(fullRef);
    if (!linkData) {
        return <span className="text-muted-foreground">{fullRef}</span>;
    }

    return (
        <span
            onClick={() => setUrl(linkData.url)}
            className={`cursor-pointer hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
        >
            {linkData.displayText}
        </span>
    );
}


